import React, { type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { CsrfTokenProvider, MessageHandlerProvider, NavBar } from 'components'
import { SessionProvider, useSessionContext } from 'session'
import { GlobalStyles, ThemeProvider } from '@mui/material'
import { themeUs, themeInternational } from 'theme'
import {
  LoginPage,
  SearchPage,
  OrganizationPage,
  OrganizationUpdatePage,
  PositionsPage,
  PeoplePage,
  PeoplePageUS,
  PersonUpdatePage,
  OrganizationAddPage,
  ServiceLevelObjectivePage,
  UsSearchPage,
  UsPersonUpdatePage
  , UsPage
  , InternationalPage
  , CommitteePage
  , MemberUpdatePage, CommitteeUpdatePage, CommitteeCreatePage, CommitteeMembersPage
  , DuplicatesPage, LeadershipOfficerListPage, PIDFPage, BuildingsPage
  , ExecutivePositionSearchPage
  , ExecutiveOrgSearchPage
  , ExecutiveOrgDetailPage
  , ExecutiveOrgMovePage
  , ExecutiveOrgCreatePage
  , ExecutiveOrgPositionsBulkPage
  , ExecutivePositionUpdatePage
  , ExecutivePositionCreatePage
} from 'pages'
import { MemberListePage } from './pages/us/member/list/member-list-page'
import { CommitteeStaffersPage } from './pages/us/committee/staffers/commitee-staffers-page'
import { LeadershipStaffersPage } from './pages/us/leadership/staffers/leadership-staffers-page'
import { LeadershipInfoPage } from './pages/us/leadership/leadership-info-page'
import { LeadershipOfficerCreatePage } from './pages/us/leadership/create/leadership-officer-create-page'

const App: React.FC = () => {
  return (
            <SessionProvider>
                <ThemeWrapper>
                <CsrfTokenProvider>
            <MessageHandlerProvider>
                    <BrowserRouter basename={process.env.PUBLIC_URL}>
                        <NavBar/>
                        <Routes>
                            <Route path="/login" element={<LoginPage/>}/>
                            <Route path="/search" element={<InternationalPage page={<SearchPage/>}/>}/>
                            <Route path="/organizations" element={<InternationalPage page={<OrganizationPage/>}/>}/>
                            <Route path="/positions/:orgId" element={<InternationalPage page={<PositionsPage/>}/>}/>
                            <Route path="/editOrganization/:orgId" element={<InternationalPage page={<OrganizationUpdatePage />} />} />
                            <Route path="/addOrganization" element={<InternationalPage page={<OrganizationAddPage/>} />} />
                            <Route path="/people" element={<InternationalPage page={<PeoplePage/>}/>}/>
                            <Route path="/editPerson/:personId" element={<InternationalPage page={<PersonUpdatePage />} />} />
                            <Route path="/slos" element={<InternationalPage page={<ServiceLevelObjectivePage/>}/>}/>
                            <Route path="/duplicates" element={<InternationalPage page={<DuplicatesPage/>}/>}/>

                          <Route path="/us/search" element={<UsPage page={<UsSearchPage />}/>}/>
                            <Route path="/us/pidf/" element={<UsPage page={<PIDFPage/>}/>}/>
                            <Route path="/us/legMember/" element={<UsPage page={<MemberListePage/>}/>}/>
                            <Route path="/us/editMember/:memberId/:cachedPersonId" element={<UsPage page={<MemberUpdatePage/>} />} />
                            <Route path="/us/people/" element={<UsPage page={<PeoplePageUS/>}/>}/>
                            <Route path="/us/leadershipOfficers/" element={<UsPage page={<LeadershipOfficerListPage/>}/>}/>
                            <Route path="/us/leadershipOfficers/create" element={<UsPage page={<LeadershipOfficerCreatePage/>}/>}/>
                            <Route path="/us/leadershipOfficers/edit/:legOfcId" element={<UsPage page={<LeadershipInfoPage/>}/>}/>
                            <Route path="/us/leadershipOfficers/staff/:legOfcId" element={<UsPage page={<LeadershipStaffersPage/>}/>}/>
                            <Route path="/us/editPerson/:personId" element={<UsPage page={<UsPersonUpdatePage />}/>}/>
                            <Route path="/us/committee/" element={<UsPage page={<CommitteePage />}/>}/>
                            <Route path="/us/committee/create/:parentId?" element={<UsPage page={<CommitteeCreatePage />}/>}/>
                            <Route path="/us/committee/edit/:comId" element={<UsPage page={<CommitteeUpdatePage />}/>}/>
                            <Route path="/us/committee/members/:comId" element={<UsPage page={<CommitteeMembersPage />}/>}/>
                            <Route path="/us/committee/staffers/:comId" element={<UsPage page={<CommitteeStaffersPage />}/>}/>
                            <Route path="/us/buildings/" element={<UsPage page={<BuildingsPage />}/>}/>
                            <Route path="/us/executives/search" element={<UsPage page={<ExecutivePositionSearchPage/>}/>}/>
                            <Route path="/us/executives/orgs" element={<UsPage page={<ExecutiveOrgSearchPage/>}/>}/>
                            <Route path="/us/executives/orgs/create" element={<UsPage page={<ExecutiveOrgCreatePage/>}/>}/>
                            <Route path="/us/executives/orgs/:parentOrgId/create" element={<UsPage page={<ExecutiveOrgCreatePage/>}/>}/>
                            <Route path="/us/executives/orgs/:execOrgId" element={<UsPage page={<ExecutiveOrgDetailPage/>}/>}/>
                            <Route path="/us/executives/orgs/:execOrgId/move" element={<UsPage page={<ExecutiveOrgMovePage/>}/>}/>
                            <Route path="/us/executives/orgs/:execOrgId/add-position" element={<UsPage page={<ExecutivePositionCreatePage/>}/>}/>
                            <Route path="/us/executives/orgs/:execOrgId/bulk-positions" element={<UsPage page={<ExecutiveOrgPositionsBulkPage/>}/>}/>
                            <Route path="/us/executives/position-update/:positionId" element={<UsPage page={<ExecutivePositionUpdatePage/>}/>}/>

                          <Route path="*" element={<Navigate to="/login" replace/>}/>
                        </Routes>
                    </BrowserRouter>
            </MessageHandlerProvider>
            </CsrfTokenProvider>
                </ThemeWrapper>

            </SessionProvider>
  )
}

interface ThemeWrapperProps {
  children: ReactNode
}
const ThemeWrapper: React.FC<ThemeWrapperProps> = ({ children }) => {
  const { sessionData } = useSessionContext()
  const theme = sessionData?.region === 'us' ? themeUs : themeInternational

  return (
    <ThemeProvider theme={theme}>
      {sessionData.keepDisabledTextBlack && <GlobalStyles styles={(theme) => ({
        '.MuiInputBase-input.Mui-disabled.Mui-disabled': { WebkitTextFillColor: theme.palette.text.primary, color: theme.palette.text.primary },
        '.MuiInputLabel-root.Mui-disabled.Mui-disabled': { color: theme.palette.text.secondary }
      })} />}
      {children}
    </ThemeProvider>
  )
}

export default App
